import { useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import MembersTab from './members'
import TransactionsTab from './transactions'
import WinnersTab from './winners'

type TabKey = 'members' | 'transactions' | 'winners'

const TABS: Array<{ key: TabKey; label: string }> = [
    { key: 'members', label: 'اعضا' },
    { key: 'transactions', label: 'تراکنش‌ها' },
    { key: 'winners', label: 'برندگان' },
]

export default function CashboxTabs({
    id,
    cashbox,
    onTabChange,
    forceTab,
    onSearch,
}: {
    id?: string
    cashbox: ICashboxDetail
    /**
     * Fired when the reader moves to another tab.
     *
     * The قرعه‌کشی landing reports it: which of اعضا / تراکنش‌ها / برندگان a
     * visitor bothered to open is one of the few honest signals that they were
     * actually reading rather than bouncing.
     */
    onTabChange?: (tab: TabKey) => void
    /**
     * Force the open tab from outside.
     *
     * The قرعه‌کشی landing needs it: a tour topic about «چه کسی پرداخت کرده»
     * has to put اعضا on screen before it can point at the list, and a topic
     * about شفافیت has to put تراکنش‌ها there. Left unset — everywhere in the
     * app — the tab stays exactly as self-governed as it was.
     */
    forceTab?: TabKey
    /**
     * Fired once the reader stops typing in the search box.
     *
     * Only the قرعه‌کشی landing passes it, and it is given the LENGTH of what
     * was typed, never the text. Someone searching the member list is reading
     * rather than skimming — that is the signal worth having — and shipping
     * the words themselves to analytics would be exporting user input for no
     * extra information.
     */
    onSearch?: (chars: number, tab: TabKey) => void
}) {
    const [active, setActive] = useState<TabKey>('members')
    const [search, setSearch] = useState('')
    const searchTimer = useRef<ReturnType<typeof setTimeout>>()

    // Debounced: one report per search, not one per keystroke. Firing on every
    // change would turn a six-letter name into six events and make the number
    // of searches meaningless.
    useEffect(() => {
        if (!onSearch) return
        clearTimeout(searchTimer.current)
        const typed = search.trim()
        if (!typed) return
        searchTimer.current = setTimeout(
            () => onSearch(typed.length, active),
            800
        )
        return () => clearTimeout(searchTimer.current)
    }, [search, active, onSearch])

    useEffect(() => {
        if (!forceTab) return
        setActive(forceTab)
        // A search typed under the previous topic would silently filter the
        // list the next topic is about to point at.
        setSearch('')
    }, [forceTab])

    return (
        <div className="bg-[#FAFAFA] min-h-[50vh]">
            <div className="sticky top-0 z-10 bg-white border-b border-divider">
                <div className="flex">
                    {TABS.map((tab) => (
                        <button
                            key={tab.key}
                            type="button"
                            onClick={() => {
                                setActive(tab.key)
                                setSearch('')
                                if (tab.key !== active) onTabChange?.(tab.key)
                            }}
                            className={classNames(
                                'flex-1 h-12 text-sm transition-colors',
                                active === tab.key
                                    ? 'text-green-1 font-bold border-b-2 border-green-1'
                                    : 'text-text-secondary border-b-2 border-transparent'
                            )}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>
            </div>

            <div className="px-4 pt-3">
                <input
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="جستجو…"
                    className="w-full h-10 rounded-[10px] border border-divider bg-white px-3 text-sm outline-none focus:border-green-1"
                />
            </div>

            {active === 'members' && (
                <MembersTab id={id} cashbox={cashbox} search={search} />
            )}
            {active === 'transactions' && (
                <TransactionsTab id={id} search={search} />
            )}
            {active === 'winners' && (
                <WinnersTab id={id} cashbox={cashbox} search={search} />
            )}
        </div>
    )
}
